import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { mockPropFirms } from '@/data/mock';
import { toast } from 'sonner';
import { BasicInfoSection } from '@/components/propfirm-editor/BasicInfoSection';
import { ChallengesSection } from '@/components/propfirm-editor/ChallengesSection';
import { FeaturesSection } from '@/components/propfirm-editor/FeaturesSection';
import { ProsConsSection } from '@/components/propfirm-editor/ProsConsSection';
import { CouponsSection } from '@/components/propfirm-editor/CouponsSection';
import { SEOSection } from '@/components/propfirm-editor/SEOSection';
import { SidebarSection } from '@/components/propfirm-editor/SidebarSection';
import { createPropFirm } from "@/services/propFirmApi";

export const PropFirmEditorPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  // State
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [logo, setLogo] = useState(null);
  const [tagline, setTagline] = useState('');
  const [description, setDescription] = useState('');
  const [country, setCountry] = useState('');
  const [foundedYear, setFoundedYear] = useState('');
  const [website, setWebsite] = useState('');
  const [affiliateUrl, setAffiliateUrl] = useState('');
  const [status, setStatus] = useState('draft');
  const [featured, setFeatured] = useState(false);
  const [challengeTypes, setChallengeTypes] = useState([]);
  const [pros, setPros] = useState([]);
  const [cons, setCons] = useState([]);
  const [allowedRules, setAllowedRules] = useState([]);
  const [notAllowedRules, setNotAllowedRules] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [platforms, setPlatforms] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [featurePriority, setFeaturePriority] = useState(1);

  // Helper to generate unique IDs
  const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Load existing firm data when editing
  useEffect(() => {
    if (id) {
      const firm = mockPropFirms.find(f => String(f.id) === String(id));
      if (firm) {
        setName(firm.name || '');
        setSlug(firm.slug || '');
        setLogo(firm.logo || null);
        setTagline(firm.tagline || '');
        setDescription(firm.description || '');
        setCountry(firm.country || '');
        setFoundedYear(firm.foundedYear?.toString() || '');
        setWebsite(firm.website || '');
        setAffiliateUrl(firm.affiliateUrl || '');
        setStatus(firm.status || 'draft');
        setFeatured(firm.featured || false);
        setFeaturePriority(firm.featurePriority || 1);
        
        // Load platforms, instruments, payment methods (convert to objects with IDs)
        setPlatforms((firm.platforms || []).map(p => typeof p === 'string' ? { id: generateId(), value: p } : p));
        setInstruments((firm.instruments || []).map(i => typeof i === 'string' ? { id: generateId(), value: i } : i));
        setPaymentMethods((firm.paymentMethods || []).map(m => typeof m === 'string' ? { id: generateId(), value: m } : m));
        setCoupons(firm.coupons || []);
        
        toast.success(`Loading ${firm.name}...`);
      } else {
        toast.error('Firm not found');
        navigate('/dashboard/firms');
      }
    }
  }, [id, navigate]);

  // Challenge handlers
  const addChallengeType = () => {
    setChallengeTypes([...challengeTypes, {
      id: Date.now(),
      name: '',
      price: '',
      maxFunding: '',
      profitSplit: '',
      steps: '2',
      drawdownType: 'Trailing',
      maxDrawdown: '',
      dailyDrawdown: '',
      leverage: '100:1',
      minTradingDays: '',
      payoutFrequency: '14D',
      profitTarget: '',
      payoutCycle: '',
      frequency: 'Always Available',
      affiliateLink: '',
    }]);
  };

  const removeChallengeType = (id) => {
    setChallengeTypes(challengeTypes.filter(c => c.id !== id));
  };

  const updateChallengeType = (id, field, value) => {
    setChallengeTypes(challengeTypes.map(c => 
      c.id === id ? { ...c, [field]: value } : c
    ));
  };

  // Generic list item handlers
  const addItem = (list, setList) => {
    setList([...list, { id: generateId(), value: '' }]);
  };

  const updateItem = (list, setList, id, value) => {
    setList(list.map(item => item.id === id ? { ...item, value } : item));
  };

  const removeItem = (list, setList, id) => {
    setList(list.filter(item => item.id !== id));
  };

  // FAQ handlers
  const addFaq = () => {
    setFaqs([...faqs, { id: generateId(), question: '', answer: '' }]);
  };

  const updateFaq = (id, field, value) => {
    setFaqs(faqs.map(faq => faq.id === id ? { ...faq, [field]: value } : faq));
  };

  const removeFaq = (id) => {
    setFaqs(faqs.filter(faq => faq.id !== id));
  };

  // Save handler
 const handleSave = async () => {
    try {

        if (!name.trim()) {
            toast.error("Firm name is required");
            return;
        }

        const payload = {

            name,

            slug,

            status: status.charAt(0).toUpperCase() + status.slice(1),

            platforms: platforms
                .map(p => p.value)
                .filter(Boolean),

            instruments: instruments
                .map(i => i.value)
                .filter(Boolean),

            pros_cons: [

                ...pros
                    .filter(item => item.value.trim())
                    .map(item => ({
                        type: "pro",
                        text: item.value
                    })),

                ...cons
                    .filter(item => item.value.trim())
                    .map(item => ({
                        type: "con",
                        text: item.value
                    }))
            ],

            faqs: faqs
                .filter(f => f.question && f.answer)
                .map(f => ({
                    question: f.question,
                    answer: f.answer
                })),

            discounts: coupons
                .filter(c => c.code && c.discount)
                .map(c => ({
                    discount_code: c.code,
                    discount_text: c.discount
                })),

            features: [

                ...allowedRules
                    .filter(r => r.value)
                    .map(r => ({
                        type: "allowed",
                        text: r.value
                    })),

                ...notAllowedRules
                    .filter(r => r.value)
                    .map(r => ({
                        type: "not_allowed",
                        text: r.value
                    }))
            ],

            challenges: challengeTypes.map(challenge => ({

                name: challenge.name,

                price: Number(challenge.price),

                max_funding: Number(challenge.maxFunding),

                profit_split: Number(challenge.profitSplit),

                profit_target: challenge.profitTarget,

                payout_cycle: challenge.payoutCycle,

                frequency: challenge.frequency

            }))

        };

        const formData = new FormData();

        formData.append("payload", JSON.stringify(payload));

        if (logo instanceof File) {
            formData.append("logo", logo);
        }

        await createPropFirm(formData);

        toast.success("Firm created successfully");

        navigate("/dashboard/firms");

    } catch (err) {

        console.log(err);

        toast.error(
            err?.response?.data?.detail ||
            err?.response?.data?.message ||
            "Unable to create firm"
        );
    }
};

  return (
    <div>
      <PageHeader
        title={id ? 'Edit Prop Firm' : 'Add Prop Firm'}
        breadcrumbs={['Admin', 'Content', 'Prop Firms', id ? 'Edit' : 'New']}
        actions={
          <Button variant="outline" onClick={() => navigate('/dashboard/firms')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Firms
          </Button>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Tabs defaultValue="basic" className="space-y-6">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="challenges">Challenges</TabsTrigger>
              <TabsTrigger value="features">Features</TabsTrigger>
              <TabsTrigger value="pros-cons">Pros & Cons</TabsTrigger>
              <TabsTrigger value="coupons">Coupons</TabsTrigger>
              <TabsTrigger value="seo">SEO</TabsTrigger>
            </TabsList>

            <TabsContent value="basic">
              <BasicInfoSection
                name={name}
                setName={setName}
                slug={slug}
                setSlug={setSlug}
                logo={logo}
                setLogo={setLogo}
                tagline={tagline}
                setTagline={setTagline}
                description={description}
                setDescription={setDescription}
                country={country}
                setCountry={setCountry}
                foundedYear={foundedYear}
                setFoundedYear={setFoundedYear}
                website={website}
                setWebsite={setWebsite}
                affiliateUrl={affiliateUrl}
                setAffiliateUrl={setAffiliateUrl}
                platforms={platforms}
                setPlatforms={setPlatforms}
                instruments={instruments}
                setInstruments={setInstruments}
                paymentMethods={paymentMethods}
                setPaymentMethods={setPaymentMethods}
                generateId={generateId}
              />
            </TabsContent>

            <TabsContent value="challenges">
              <ChallengesSection
                challengeTypes={challengeTypes}
                setChallengeTypes={setChallengeTypes}
                updateChallengeType={updateChallengeType}
                removeChallengeType={removeChallengeType}
                addChallengeType={addChallengeType}
              />
            </TabsContent>

            <TabsContent value="features">
              <FeaturesSection
                allowedRules={allowedRules}
                setAllowedRules={setAllowedRules}
                notAllowedRules={notAllowedRules}
                setNotAllowedRules={setNotAllowedRules}
                addItem={addItem}
                updateItem={updateItem}
                removeItem={removeItem}
              />
            </TabsContent>

            <TabsContent value="pros-cons">
              <ProsConsSection
                pros={pros}
                setPros={setPros}
                cons={cons}
                setCons={setCons}
                addItem={addItem}
                updateItem={updateItem}
                removeItem={removeItem}
              />
            </TabsContent>

            <TabsContent value="coupons">
              <CouponsSection
                coupons={coupons}
                setCoupons={setCoupons}
                generateId={generateId}
              />
            </TabsContent>

            <TabsContent value="seo">
              <SEOSection
                metaTitle={metaTitle}
                setMetaTitle={setMetaTitle}
                metaDescription={metaDescription}
                setMetaDescription={setMetaDescription}
                faqs={faqs}
                addFaq={addFaq}
                updateFaq={updateFaq}
                removeFaq={removeFaq}
              />
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-6">
          <SidebarSection
            id={id}
            status={status}
            setStatus={setStatus}
            featured={featured}
            setFeatured={setFeatured}
            featurePriority={featurePriority}
            setFeaturePriority={setFeaturePriority}
            handleSave={handleSave}
          />
        </div>
      </div>
    </div>
  );
};
